import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  alpha,
  useTheme,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Work as WorkIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import jobseekerapi from '../../../../services/jobseekerApi';

// Types
interface Education {
  degree?: string;
  institution?: string;
  year?: string;
  [key: string]: any;
}

interface Experience {
  company?: string;
  position?: string;
  duration?: string;
  [key: string]: any;
}

interface JobSeeker {
  _id: string;
  job_seeker_code: string;
  first_name: string;
  last_name: string;
  country_code: number;
  email_id: string;
  reference: string;
  resume: string | null;
  privacy_policy_accept: boolean;
  profile_image: string | null;
  skills: string[];
  stripe_customer_id: string | null;
  is_email_verified: boolean;
  is_process_ongoing: boolean;
  total_ongoing_application: number;
  ongoing_application: number;
  is_active: boolean;
  is_user_login: boolean;
  is_deleted: boolean;
  education: Education[];
  experience: Experience[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  [key: string]: any;
}

interface ConversationMessage {
  _id: string;
  conversion_id: string;
  type: 'user' | 'AI';
  message: string;
  conversion_type: string;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface TranscriptionData {
  data?: ConversationMessage[];
  [key: string]: any;
}

const Transcription: React.FC = () => {
  const theme = useTheme();

  // State variables
  const [jobSeekers, setJobSeekers] = useState<JobSeeker[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState<JobSeeker | null>(null);
  const [transcriptionData, setTranscriptionData] = useState<TranscriptionData | null>(null);
  const [loadingTranscription, setLoadingTranscription] = useState<boolean>(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [availableJobSeekers, setAvailableJobSeekers] = useState<any[]>([]);
  const [selectedJobSeekerId, setSelectedJobSeekerId] = useState<string>('');

  // Fetch job seekers on component mount and when filters/pagination changes
  useEffect(() => {
    fetchJobSeekers();
  }, [page, rowsPerPage, searchQuery, selectedJobSeekerId]);

  useEffect(() => {
    fetchAllJobSeekers();
  }, []);

  // Function to fetch all job seekers for the dropdown
  const fetchAllJobSeekers = async () => {
    try {
      const response = await jobseekerapi.post('/jobseeker/jobseekerList', {
        startToken: 1,
        endToken: 100000,
        search: searchQuery,
      });
      if (response?.data?.status) {
        setAvailableJobSeekers(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching all job seekers:', err);
    }
  };

  // Function to fetch job seekers
  const fetchJobSeekers = async () => {
    setLoading(true);
    setError(null);

    try {
      // startToken is page number (1-indexed), endToken is number of records per page
      // const startToken = page + 1;
      const startToken = page + 1;
      const endToken = rowsPerPage;

      const response = await jobseekerapi.post('/jobseeker/transcriptionList', {
        startToken,
        endToken,
        search: searchQuery,
        jobseeker_id: selectedJobSeekerId
      });

      if (response?.data?.status) {
        setJobSeekers(response.data.data || []);
        setTotalRows(response.data.record_count || 0);
      } else {
        setJobSeekers([]);
        setTotalRows(0);
        setError('Failed to fetch job seekers');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch job seekers');
      console.error('Error fetching job seekers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Reset search
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedJobSeekerId('');
    setPage(0);
  };

  // Function to fetch transcription data
  const fetchTranscriptionData = async (threadId: string) => {
    setLoadingTranscription(true);
    setTranscriptionError(null);

    try {
      const response = await jobseekerapi.get(`/jobseeker/transcription/${threadId}`);
      // const response = await jobseekerapi.get(`/jobseeker/transcription/sess_CWmQ9O0AyveThbhlZDeCy`);

      if (response?.data?.status) {
        setTranscriptionData({ data: response.data.data || [] });
      } else {
        setTranscriptionError('Failed to fetch transcription data');
      }
    } catch (err: any) {
      setTranscriptionError(err?.response?.data?.message || 'Failed to fetch transcription data');
      console.error('Error fetching transcription data:', err);
    } finally {
      setLoadingTranscription(false);
    }
  };

  // Handle modal open
  const handleViewDetails = (jobSeeker: JobSeeker) => {
    setSelectedJobSeeker(jobSeeker);
    setOpenModal(true);
    setTranscriptionData(null);
    setTranscriptionError(null);

    // Fetch transcription data if thread_id is available
    if (jobSeeker.thread_id) {
      fetchTranscriptionData(jobSeeker.thread_id);
    } else {
      setTranscriptionError('Thread ID not available');
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedJobSeeker(null);
    setTranscriptionData(null);
    setTranscriptionError(null);
  };

  // Render status chip based on is_active and is_email_verified
  const renderStatusChip = (jobSeeker: JobSeeker) => {
    if (!jobSeeker.is_active) {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    if (!jobSeeker.is_email_verified) {
      return <Chip label="Unverified" color="warning" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  // Format date
  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit', // optional
      hour12: true,      // set false for 24-hour format
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          Candidate Transcription
        </Typography>
      </Box>

      {/* Search Filter */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '20% 70% auto' },
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Autocomplete
          fullWidth
          size="small"
          options={availableJobSeekers}
          getOptionLabel={(option: any) =>
            option.jobseeker_name ||
            (option.first_name ? `${option.first_name} ${option.last_name || ''}` : '') ||
            option.email_id ||
            ''
          }
          value={availableJobSeekers.find(js => js._id === selectedJobSeekerId) || null}
          onChange={(_event, newValue) => {
            setSelectedJobSeekerId(newValue ? newValue._id : '');
            setPage(0);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Candidate" placeholder="Select a candidate" />
          )}
        />
        <TextField
          label="Search Candidate Transcription"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by Candidate name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={resetFilters}
          sx={{
            height: 40,
            whiteSpace: 'nowrap',
            minWidth: 'fit-content'
          }}
        >
          Reset
        </Button>
      </Paper>

      {/* Job Seekers Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job Seeker Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Prompt Type</TableCell>
                <TableCell>Date and Time</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : jobSeekers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    {searchQuery ? 'No data found matching your search' : 'No data found'}
                  </TableCell>
                </TableRow>
              ) : (
                jobSeekers.map((jobSeeker) => (
                  <TableRow key={jobSeeker._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={jobSeeker.profile_image || undefined}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: theme.palette.primary.main,
                            fontSize: '0.875rem',
                          }}
                        >
                          {jobSeeker.jobseeker_name ? jobSeeker.jobseeker_name.charAt(0).toUpperCase() : <PersonIcon fontSize="small" />}
                        </Avatar>
                        <Typography sx={{ fontWeight: 500 }}>
                          {jobSeeker.jobseeker_name
                            ? `${jobSeeker.jobseeker_name}`
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {jobSeeker.jobseeker_email_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={jobSeeker?.type || 'N/A'}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(jobSeeker.createdAt)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleViewDetails(jobSeeker)}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Transcription Prompt
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedJobSeeker && (
            <Box>
              {/* Transcription Conversation Section */}
              <Box sx={{ mb: 3 }}>
                {loadingTranscription ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : transcriptionError ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.error.main, 0.05),
                      borderColor: theme.palette.error.main,
                    }}
                  >
                    <Typography variant="body2" color="error">
                      {transcriptionError}
                    </Typography>
                  </Paper>
                ) : transcriptionData?.data && transcriptionData.data.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: '500px',
                      overflow: 'auto',
                      p: 2,
                      backgroundColor: alpha(theme.palette.grey[500], 0.03),
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {transcriptionData.data.map((msg, index) => (
                      <Box
                        key={msg._id || index}
                        sx={{
                          display: 'flex',
                          justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '75%',
                            display: 'flex',
                            flexDirection: msg.type === 'user' ? 'row-reverse' : 'row',
                            gap: 1.5,
                            alignItems: 'flex-start',
                          }}
                        >
                          {/* Avatar */}
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: msg.type === 'user'
                                ? theme.palette.primary.main
                                : theme.palette.secondary.main,
                              fontSize: '0.875rem',
                            }}
                          >
                            {msg.type === 'user' ? <PersonIcon fontSize="small" /> : 'AI'}
                          </Avatar>

                          {/* Message Bubble */}
                          <Paper
                            elevation={1}
                            sx={{
                              p: 2,
                              backgroundColor: msg.type === 'user'
                                ? theme.palette.primary.main
                                : theme.palette.grey[100],
                              color: msg.type === 'user'
                                ? theme.palette.primary.contrastText
                                : theme.palette.text.primary,
                              borderRadius: 2,
                              borderTopRightRadius: msg.type === 'user' ? 0 : 2,
                              borderTopLeftRadius: msg.type === 'AI' ? 0 : 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {msg.message}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 1,
                                opacity: 0.7,
                                fontSize: '0.7rem',
                              }}
                            >
                              {new Date(msg.createdAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          </Paper>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.grey[500], 0.05),
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No conversation data available
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Transcription;
